/**
 * Pure helpers for the fleet UI: version drift, rollout progress and the badges a machine card
 * shows. No React here, so it is tested directly. (Drain is the scheduler's: features/scheduler.)
 */
import { CHANNEL_LABEL, RING_LABEL, ROLLOUT_STATUS_LABEL, UPDATE_HOLD_LABEL, UPDATE_STAGE_LABEL, activeUpdateFreeze, compareVersions, stageName } from '@orgus/protocol';
import type { FleetMachine, FleetState, Machine, Rollout, RolloutTarget, UpdateHold, UpdateHoldReason, UpdateStage } from '@orgus/protocol';
import type { Tone } from '@orgus/ui';

export interface VersionGroup {
  version: string;
  count: number;
  drift: 'current' | 'behind' | 'ahead' | 'unknown';
  names: string[];
}

/** Machines grouped by agent version, newest first. */
export function versionGroups(machines: Machine[], latest: string | null): VersionGroup[] {
  const by = new Map<string, VersionGroup>();
  for (const m of machines) {
    const v = m.agentVersion || 'unknown';
    const g = by.get(v) ?? { version: v, count: 0, drift: 'unknown' as VersionGroup['drift'], names: [] };
    g.count++;
    g.names.push(m.name);
    if (v !== 'unknown' && latest) {
      const d = compareVersions(v, latest);
      g.drift = d === 0 ? 'current' : d < 0 ? 'behind' : 'ahead';
    }
    by.set(v, g);
  }
  return [...by.values()].sort((a, b) => (a.version === 'unknown' ? 1 : b.version === 'unknown' ? -1 : compareVersions(b.version, a.version)));
}

export function belowMinimum(version: string, min: string | null | undefined): boolean {
  return !!min && compareVersions(version || '0', min) < 0;
}

export const DRIFT_LABEL: Record<VersionGroup['drift'], string> = {
  current: 'Latest',
  behind: 'Behind',
  ahead: 'Newer than the latest release',
  unknown: 'Unknown',
};

export function stageTone(stage: UpdateStage): Tone {
  if (stage === 'done') return 'passed';
  if (stage === 'failed' || stage === 'rolled-back') return 'failed';
  if (stage === 'waiting-for-work') return 'waiting';
  return 'working';
}

export function stageLabel(stage: UpdateStage): string {
  return UPDATE_STAGE_LABEL[stage] ?? stage;
}

export function targetTone(state: RolloutTarget['state']): Tone {
  return state === 'done' ? 'passed' : state === 'failed' ? 'failed' : state === 'skipped' ? 'idle' : state === 'pending' ? 'queued' : 'working';
}

export const TARGET_LABEL: Record<RolloutTarget['state'], string> = {
  pending: 'Waiting',
  offered: 'Offered',
  updating: 'Updating',
  done: 'Updated',
  failed: 'Failed',
  skipped: 'Update by hand',
};

export function rolloutTone(r: Rollout): Tone {
  if (r.status === 'halted') return 'failed';
  if (r.status === 'awaiting-promotion') return 'waiting';
  if (r.status === 'cancelled') return 'idle';
  if (r.status === 'done') return r.targets.some((t) => t.state === 'failed') ? 'waiting' : 'passed';
  return 'working';
}

export function rolloutLabel(r: Rollout): string {
  if (r.status === 'running' && r.plan) return r.plan.soakUntil ? `Soaking after ${stageName(r.plan.stages, r.plan.wave).toLowerCase()}` : `Rolling out: ${stageName(r.plan.stages, r.plan.wave).toLowerCase()}`;
  if (r.status === 'running') return `Rolling out to the ${r.stage === 'test' ? 'test ring' : 'fleet'}`;
  return ROLLOUT_STATUS_LABEL[r.status];
}

export interface RingProgress {
  ring: 'test' | 'fleet';
  label: string;
  done: number;
  failed: number;
  open: number;
  skipped: number;
  total: number;
}

export function ringProgress(r: Rollout): RingProgress[] {
  return (['test', 'fleet'] as const)
    .map((ring) => {
      const ts = r.targets.filter((t) => t.ring === ring);
      return {
        ring,
        label: RING_LABEL[ring],
        done: ts.filter((t) => t.state === 'done').length,
        failed: ts.filter((t) => t.state === 'failed').length,
        skipped: ts.filter((t) => t.state === 'skipped').length,
        open: ts.filter((t) => t.state === 'pending' || t.state === 'offered' || t.state === 'updating').length,
        total: ts.length,
      };
    })
    .filter((p) => p.total > 0);
}

export function fleetOf(state: FleetState | null, machineId: string): FleetMachine | null {
  return state?.machines.find((m) => m.machineId === machineId) ?? null;
}

export interface FleetBadge {
  key: string;
  tone: Tone;
  label: string;
  title: string;
}

/** The fleet badges on a machine card, most important first. */
export function machineBadges(m: Machine, f: FleetMachine | null, o: { latest: string | null; minAgentVersion: string | null }): FleetBadge[] {
  const out: FleetBadge[] = [];
  const version = m.agentVersion;
  if (belowMinimum(m.agentVersion, o.minAgentVersion)) {
    out.push({ key: 'min', tone: 'failed', label: 'Below minimum', title: `Agent ${m.agentVersion || 'unknown'} is below this hub's minimum ${o.minAgentVersion}: write actions are refused until it updates. Reads keep working.` });
  }
  if (f?.update && f.update.stage !== 'done' && f.update.stage !== 'failed' && f.update.stage !== 'rolled-back') {
    out.push({ key: 'update', tone: stageTone(f.update.stage), label: stageLabel(f.update.stage), title: `Updating to agent ${f.update.version}.` });
  } else if (f?.update && (f.update.stage === 'failed' || f.update.stage === 'rolled-back') && compareVersions(version, f.update.version) !== 0) {
    out.push({ key: 'update', tone: 'failed', label: f.update.stage === 'rolled-back' ? 'Rolled back' : 'Update failed', title: f.update.message ?? `The update to ${f.update.version} did not finish.` });
  } else if (f?.hold && isProblemHold(f.hold)) {
    out.push({ key: 'hold', tone: NEEDS_YOU.has(f.hold.reason) ? 'waiting' : 'neutral', label: holdLabel(f.hold), title: f.hold.detail });
  } else if (o.latest && version && compareVersions(version, o.latest) < 0) {
    out.push({ key: 'drift', tone: 'neutral', label: 'Update available', title: `Runs agent ${version}; the latest release is ${o.latest}.${f?.hold ? ` ${f.hold.detail}` : ''}` });
  }
  if (f?.ring === 'test') out.push({ key: 'ring', tone: 'accent', label: 'Test ring', title: 'Gets new agent versions before the rest of the fleet.' });
  return out;
}

// ── Automatic updates (the update policy) ─────────────────────────────────────

/** The one line that says what automatic updates are doing: status (icon plus label) and a short detail. */
export interface AutoLine {
  tone: 'success' | 'attention' | 'neutral' | 'working';
  icon: 'done' | 'paused' | 'stopped' | 'working' | 'queued';
  label: string;
  /** Who and why, or what comes next; null when there is nothing to add. */
  detail: string | null;
  /** When a pause or freeze ends by itself. */
  until: number | null;
}

export function autoLine(s: FleetState, now: number): AutoLine {
  const p = s.policy;
  if (!s.feed) return { tone: 'neutral', icon: 'stopped', label: 'Updates by hand', detail: 'No release feed on this server', until: null };
  if (!p) return { tone: 'neutral', icon: 'stopped', label: 'Updates by hand', detail: null, until: null };
  const channel = CHANNEL_LABEL[p.channel].toLowerCase();
  if (p.paused && (p.paused.until === null || p.paused.until > now)) {
    return { tone: 'attention', icon: 'paused', label: 'Automatic updates paused', detail: `By ${p.paused.by}${p.paused.reason ? `: ${p.paused.reason}` : ''}`, until: p.paused.until };
  }
  if (p.mode === 'manual') return { tone: 'neutral', icon: 'stopped', label: 'Automatic updates off', detail: `${CHANNEL_LABEL[p.channel]} channel, rolled out by an admin`, until: null };
  const freeze = activeUpdateFreeze(p.freezes, now);
  if (freeze) return { tone: 'attention', icon: 'paused', label: 'Update freeze', detail: freeze.reason, until: freeze.until };
  if (s.rollout?.startedBy === 'policy') return { tone: 'working', icon: 'working', label: `Updating to ${s.rollout.version}`, detail: `${channel} channel, in stages`, until: null };
  return { tone: 'success', icon: 'done', label: 'Automatic updates on', detail: `${CHANNEL_LABEL[p.channel]} channel`, until: null };
}

/** The stages of a staged rollout, each with its progress, for the stage track. */
export interface StageProgress {
  index: number;
  name: string;
  done: number;
  failed: number;
  open: number;
  total: number;
  state: 'done' | 'current' | 'soaking' | 'next';
}

export function stageProgress(r: Rollout): StageProgress[] {
  const plan = r.plan;
  if (!plan) return [];
  return plan.stages.map((_, i) => {
    const ts = r.targets.filter((t) => t.wave === i);
    const state: StageProgress['state'] = i < plan.wave ? 'done' : i === plan.wave ? (plan.soakUntil ? 'soaking' : 'current') : 'next';
    return {
      index: i,
      name: stageName(plan.stages, i),
      done: ts.filter((t) => t.state === 'done').length,
      failed: ts.filter((t) => t.state === 'failed').length,
      open: ts.filter((t) => t.state === 'pending' || t.state === 'offered' || t.state === 'updating').length,
      total: ts.length,
      state,
    };
  });
}

/** Holds a person should look at (the machine itself refuses, or this version failed there). */
const PROBLEM_HOLDS: ReadonlySet<UpdateHoldReason> = new Set(['not-updatable', 'keys', 'no-build', 'rolled-back', 'failed', 'halted', 'not-reported', 'origin-mismatch']);
export const isProblemHold = (h: UpdateHold | null | undefined): boolean => !!h && PROBLEM_HOLDS.has(h.reason);
/** Of those, the ones that need a person (amber); the rest are facts of the machine (grey). */
const NEEDS_YOU: ReadonlySet<UpdateHoldReason> = new Set(['keys', 'rolled-back', 'failed', 'halted', 'origin-mismatch']);

export function holdLabel(h: UpdateHold): string {
  return UPDATE_HOLD_LABEL[h.reason] ?? h.reason;
}

/** Choices for the settings form: shares and soaks people actually pick. */
export const CANARY_SHARES = [1, 2, 5, 10];
export const EARLY_SHARES = [10, 25, 50];
export const SOAKS_MS = [0, 30 * 60_000, 3_600_000, 4 * 3_600_000, 12 * 3_600_000, 24 * 3_600_000];
export const HALT_RATES = [0.05, 0.1, 0.2, 0.5];
export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
